import { openModal } from './openModal'

// Click-delegation that turns a `[data-plg-modal-label]` element click
// into an openModal call. Works polymorphically with two trigger
// shapes so callers can pick whichever fits their context:
//
//   - Anchor (v6 + FLP today): URL on `href`, target="_blank" for
//     right-click / no-JS fallback. JS intercepts and routes to
//     openModal.
//
//       <a href="/widgets/organizations?…" target="_blank"
//          data-plg-modal-label="aria-label string">
//         Multiple Nonprofits
//       </a>
//
//   - Button (v7 today, or anywhere "trigger a dialog" semantics are
//     preferred): URL on `data-plg-modal-src` since buttons have no
//     href.
//
//       <button type="button"
//               data-plg-modal-label="aria-label string"
//               data-plg-modal-src="/widgets/organizations?…">
//         Multiple Nonprofits
//       </button>
//
// Optional `data-plg-modal-id` keys renderModal's per-id cache so a
// reopen reuses the existing dialog instead of fetching the iframe
// content again. Convention is `<type>:<key>` (`multi-org:pi-xyz`,
// `share:pi-xyz`) so a future wildcard interceptor can match by type.
// Omit when each open should produce a fresh dialog.
//
// Optional `data-plg-modal-max-width` overrides the dialog body's
// default 640px max-width on a per-modal basis (e.g. `"30rem"` for a
// narrower confirmation). Author in `rem` for consistency — renderModal
// converts it to px against our 16px theme root so the modal is the
// same physical size regardless of the host page's root font-size.
// Other CSS length values pass through untouched. Omit to keep the
// default.
//
// Loaded in two runtime contexts:
//   - host page (widget.js#init): catches clicks on FLP / partner
//     pages that include our embed script. openModal here sees
//     parent === self (host is the top frame), so it renders the
//     modal locally.
//   - donate widget iframe (donate_v6 pack): catches clicks inside
//     the iframe. openModal here sees parent !== self and either
//     routes to the parent via the channel or falls back to in-
//     iframe rendering.
//
// Returns a teardown so widget.js#init's hot-restart path can unwire
// the listener cleanly; module-only invocations can ignore it.
export const registerOpenModalTriggers = () => {
  const handler = e => {
    const trigger = e.target.closest('[data-plg-modal-label]')
    if (!trigger) return
    // No src to render — let the native click flow through instead
    // of preventDefault'ing into a broken modal. A `<button>` with
    // no data-plg-modal-src or an `<a>` with no href both land here;
    // the author sees nothing happen (button) or follows the bad
    // anchor (no href = no-op), either way a clear signal that the
    // trigger is misconfigured.
    const src = trigger.dataset.plgModalSrc || trigger.href
    if (!src) return
    e.preventDefault()
    openModal({
      src,
      ariaLabel: trigger.dataset.plgModalLabel,
      // Derive the locale from the nearest [lang] ancestor (same
      // pattern createWidget.js uses to seed widget URLs). The host
      // page's <html lang> typically carries it; per-component lang
      // attributes can override for mixed-language pages. The locale
      // is threaded through to renderModal so the dialog's chrome
      // direction matches the iframe content.
      locale: trigger.closest('[lang]')?.getAttribute('lang'),
      id: trigger.dataset.plgModalId,
      maxWidth: trigger.dataset.plgModalMaxWidth,
    })
  }
  document.addEventListener('click', handler)
  return () => document.removeEventListener('click', handler)
}
